// @ts-strict-ignore
import React from 'react'
import { FetchNextPageOptions, InfiniteQueryObserverResult } from '@tanstack/react-query'
import capitalize from 'lodash-es/capitalize'
import { useTranslation } from 'react-i18next'
import { Contact, ContactPaymentInstrument, CryptoContactInstrument } from '@node-space/types'
import { ActionListItem, Avatar, Box, Highlight, Tooltip } from '@node-space/ui-components'
import { ellipseMiddleText } from '@node-space/utils'
import { getContactHighlightLabel } from 'pages/Contacts/utils/utils'
import { ContactOrPaymentInstrumentList } from './ContactOrPaymentInstrumentList'

export interface ContactInstrumentsListProps {
  contacts: ContactPaymentInstrument[]
  isFetchingNextPage: boolean
  hasLoadedMore: boolean
  hasNextPage: boolean
  setHasLoadedMore: React.Dispatch<React.SetStateAction<boolean>>
  fetchNextPage: (
    options?: FetchNextPageOptions
  ) => Promise<InfiniteQueryObserverResult<ContactPaymentInstrument[], Error>>
  handleContactSelect: (contactPaymentInstrument: ContactPaymentInstrument) => void
}

export const ContactInstrumentsListV1 = ({
  contacts,
  isFetchingNextPage,
  hasLoadedMore,
  hasNextPage,
  handleContactSelect,
  setHasLoadedMore,
  fetchNextPage,
}: ContactInstrumentsListProps) => {
  const { t } = useTranslation()

  return (
    <ContactOrPaymentInstrumentList
      list={contacts}
      isFetchingNextPage={isFetchingNextPage}
      hasLoadedMore={hasLoadedMore}
      hasNextPage={hasNextPage}
      setHasLoadedMore={setHasLoadedMore}
      fetchNextPage={fetchNextPage}
      getItemKey={item => item.contact?.id}
      listItemRenderer={(item, index) => {
        const contact: Contact = item?.contact
        const instrument = item?.paymentInstrument as CryptoContactInstrument

        return (
          <ActionListItem
            key={contact?.id}
            id={contact?.id}
            title={contact?.name}
            testId={`contacts-step-list-item-${index}`}
            HeaderHighlightLeft={
              <Highlight type="neutral" variant="emphasis">
                {getContactHighlightLabel(contact, t)}
              </Highlight>
            }
            Avatar={<Avatar size="default" type="entity" name={contact?.name.toLowerCase()} />}
            BodyPre={
              <p className="text-label min-w-[0px] line-clamp-1">{instrument?.description}</p>
            }
            onItemClick={() => handleContactSelect(item)}
            BodyPost={
              <Tooltip
                bodyContent={`${instrument?.currency} (${capitalize(instrument?.network)}) - ${ellipseMiddleText(instrument?.address, 6, 6, '*')}`}
                type="simple"
                position="bottom"
              >
                <Box className="cursor-pointer">
                  <p className="text-body-regular whitespace-nowrap">{instrument?.currency}</p>
                </Box>
              </Tooltip>
            }
          />
        )
      }}
    />
  )
}
